// BulkPriceTableList.js

import React, { useEffect, useState } from "react";

import { Table, Button, Form, Spinner } from "react-bootstrap";

import {
  FiArrowLeft,
  FiChevronDown,
  FiChevronRight,
  FiPackage,
} from "react-icons/fi";

import styles from "./BulkPriceTableList.module.css";

const BulkPriceTableList = ({
  data = [],
  onBack,
  subLoading,
  setNewSubData,
  selectedProduct,
}) => {
  const [productList, setProductList] = useState([]);

  const [expandedRow, setExpandedRow] = useState(null);

  // =========================
  // HEADER INPUTS
  // =========================
  const [mainHeader, setMainHeader] = useState({
    base_rate: "",
    margin: "",
    sale_rate: "",
    product_mrp: "",
  });

  // =========================
  // DEFAULT DATA LOAD
  // =========================
  useEffect(() => {
    const updatedData = (data || []).map((item) => ({
      ...item,

      // MAIN PRODUCT DEFAULTS
      base_rate: item?.sizes?.[0]?.ps_price || "",

      sale_rate: item?.sizes?.[0]?.ps_whlprice || "",

      product_mrp: item?.sizes?.[0]?.ps_mrp || "",

      margin: item.margin || "",

      // SIZE DATA
      sizes: (item.sizes || []).map((size) => ({
        ...size,

        base_rate: size.ps_price || "",

        sale_rate: size.ps_whlprice || "",

        product_mrp: size.ps_mrp || "",

        margin: size.margin || "",
      })),
    }));

    setProductList(updatedData);
  }, [data]);

  // =========================
  // RETURN DATA
  // =========================
  useEffect(() => {
    if (setNewSubData) {
      setNewSubData(productList);
    }
  }, [productList, setNewSubData]);

  // =========================
  // CALCULATE VALUES
  // =========================
  const calculatePricing = (baseRate, margin) => {
    const base = Number(baseRate || 0);

    const marginPercent = Number(margin || 0);

    // SALE RATE
    const saleRate = base + (base * marginPercent) / 100;

    const finalSaleRate = Math.ceil(saleRate);

    // PRODUCT MRP
    const productMrp = Math.ceil(finalSaleRate * 2);

    return {
      sale_rate: finalSaleRate,
      product_mrp: productMrp,
    };
  };

  // =========================
  // TOGGLE ROW
  // =========================
  const toggleRow = (subid) => {
    setExpandedRow((prev) => (prev === subid ? null : subid));
  };

  // =========================
  // PRODUCT CHANGE
  // =========================
  const handleProductChange = (subid, field, value) => {
    setProductList((prev) =>
      prev.map((item) => {
        if (item.subid !== subid) return item;

        let updatedItem = {
          ...item,
          [field]: value,
        };

        // AUTO CALCULATE
        if (field === "base_rate" || field === "margin") {
          const pricing = calculatePricing(
            field === "base_rate" ? value : updatedItem.base_rate,

            field === "margin" ? value : updatedItem.margin
          );

          updatedItem.sale_rate = pricing.sale_rate;

          updatedItem.product_mrp = pricing.product_mrp;
        }

        // UPDATE ALL SUB ROWS
        updatedItem.sizes = (item.sizes || []).map((size) => {
          let updatedSize = {
            ...size,
            [field]: value,
          };

          if (field === "base_rate" || field === "margin") {
            const pricing = calculatePricing(
              field === "base_rate" ? value : size.base_rate,

              field === "margin" ? value : size.margin
            );

            updatedSize.sale_rate = pricing.sale_rate;

            updatedSize.product_mrp = pricing.product_mrp;
          }

          return updatedSize;
        });

        return updatedItem;
      })
    );
  };

  // =========================
  // SIZE CHANGE
  // =========================
  const handleSizeChange = (subid, sizeId, field, value) => {
    setProductList((prev) =>
      prev.map((item) => {
        if (item.subid !== subid) return item;

        return {
          ...item,

          sizes: (item.sizes || []).map((size) => {
            if (size.id !== sizeId) return size;

            let updatedSize = {
              ...size,
              [field]: value,
            };

            // AUTO CALCULATE
            if (field === "base_rate" || field === "margin") {
              const pricing = calculatePricing(
                field === "base_rate" ? value : updatedSize.base_rate,

                field === "margin" ? value : updatedSize.margin
              );

              updatedSize.sale_rate = pricing.sale_rate;

              updatedSize.product_mrp = pricing.product_mrp;
            }

            return updatedSize;
          }),
        };
      })
    );
  };

  // =========================
  // HEADER CHANGE
  // =========================
  const handleHeaderChange = (field, value) => {
    setMainHeader((prev) => ({
      ...prev,
      [field]: value,
    }));

    setProductList((prev) =>
      prev.map((item) => {
        let updatedItem = {
          ...item,
          [field]: value,
        };

        // AUTO CALCULATE
        if (field === "base_rate" || field === "margin") {
          const pricing = calculatePricing(
            field === "base_rate" ? value : item.base_rate,

            field === "margin" ? value : item.margin
          );

          updatedItem.sale_rate = pricing.sale_rate;

          updatedItem.product_mrp = pricing.product_mrp;
        }

        // UPDATE ALL SIZE ROWS
        updatedItem.sizes = (item.sizes || []).map((size) => {
          let updatedSize = {
            ...size,
            [field]: value,
          };

          if (field === "base_rate" || field === "margin") {
            const pricing = calculatePricing(
              field === "base_rate" ? value : size.base_rate,

              field === "margin" ? value : size.margin
            );

            updatedSize.sale_rate = pricing.sale_rate;

            updatedSize.product_mrp = pricing.product_mrp;
          }

          return updatedSize;
        });

        return updatedItem;
      })
    );
  };

  return (
    <div className={styles.wrapper}>
      {/* HEADER */}
      <div className={styles.header}>
        <Button className={styles.backBtn} onClick={onBack}>
          <FiArrowLeft size={16} />
          <span>Back</span>
        </Button>

        <div className={styles.selectedInfo}>{selectedProduct}</div>
      </div>

      {/* TABLE */}
      <div className={styles.tableCard}>
        <div className={styles.tableHeader}>Product Price List</div>

        <div className={styles.tableScroll}>
          {subLoading ? (
            <div className={styles.loaderBox}>
              <Spinner animation="border" variant="primary" />

              <div className="mt-2">Loading products...</div>
            </div>
          ) : (
            <Table bordered hover size="sm" className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.productTh}>Product</th>

                  {/* BASE RATE */}
                  <th>
                    <div className={styles.thInputBox}>
                      <div className={styles.thTitle}>Base Rate</div>

                      <Form.Control
                        size="sm"
                        type="number"
                        placeholder="All"
                        value={mainHeader.base_rate}
                        className={styles.headerInput}
                        onChange={(e) =>
                          handleHeaderChange("base_rate", e.target.value)
                        }
                      />
                    </div>
                  </th>

                  {/* MARGIN */}
                  <th>
                    <div className={styles.thInputBox}>
                      <div className={styles.thTitle}>Margin %</div>

                      <Form.Control
                        size="sm"
                        type="number"
                        placeholder="All"
                        value={mainHeader.margin}
                        className={styles.headerInput}
                        onChange={(e) =>
                          handleHeaderChange("margin", e.target.value)
                        }
                      />
                    </div>
                  </th>

                  {/* SALE RATE */}
                  <th>
                    <div className={styles.thInputBox}>
                      <div className={styles.thTitle}>Sale Rate</div>

                      <Form.Control
                        size="sm"
                        type="number"
                        placeholder="All"
                        value={mainHeader.sale_rate}
                        className={styles.headerInput}
                        onChange={(e) =>
                          handleHeaderChange("sale_rate", e.target.value)
                        }
                      />
                    </div>
                  </th>

                  {/* PRODUCT MRP */}
                  <th>
                    <div className={styles.thInputBox}>
                      <div className={styles.thTitle}>Product MRP</div>

                      <Form.Control
                        size="sm"
                        type="number"
                        placeholder="All"
                        value={mainHeader.product_mrp}
                        className={styles.headerInput}
                        onChange={(e) =>
                          handleHeaderChange("product_mrp", e.target.value)
                        }
                      />
                    </div>
                  </th>
                </tr>
              </thead>

              <tbody>
                {productList?.length > 0 ? (
                  productList.map((item) => (
                    <React.Fragment key={item.subid}>
                      {/* MAIN ROW */}
                      <tr
                        className={
                          expandedRow === item.subid ? styles.activeRow : ""
                        }
                      >
                        {/* PRODUCT */}
                        <td
                          onClick={() => toggleRow(item.subid)}
                          className={styles.productTd}
                        >
                          <div className={styles.productCell}>
                            <div className={styles.productLeft}>
                              <div className={styles.expandIcon}>
                                {expandedRow === item.subid ? (
                                  <FiChevronDown />
                                ) : (
                                  <FiChevronRight />
                                )}
                              </div>

                              <FiPackage className={styles.packageIcon} />

                              <span className={styles.productName}>
                                {item.subproduct_name}
                              </span>
                            </div>
                          </div>
                        </td>

                        {/* BASE RATE */}
                        <td>
                          <Form.Control
                            size="sm"
                            type="number"
                            placeholder="Base"
                            value={item.base_rate || ""}
                            className={styles.rowInput}
                            onChange={(e) =>
                              handleProductChange(
                                item.subid,
                                "base_rate",
                                e.target.value
                              )
                            }
                          />
                        </td>

                        {/* MARGIN */}
                        <td>
                          <Form.Control
                            size="sm"
                            type="number"
                            placeholder="%"
                            value={item.margin || ""}
                            className={styles.rowInput}
                            onChange={(e) =>
                              handleProductChange(
                                item.subid,
                                "margin",
                                e.target.value
                              )
                            }
                          />
                        </td>

                        {/* SALE RATE */}
                        <td>
                          <Form.Control
                            size="sm"
                            type="number"
                            placeholder="Sale"
                            value={item.sale_rate || ""}
                            className={styles.rowInput}
                            onChange={(e) =>
                              handleProductChange(
                                item.subid,
                                "sale_rate",
                                e.target.value
                              )
                            }
                          />
                        </td>

                        {/* PRODUCT MRP */}
                        <td>
                          <Form.Control
                            size="sm"
                            type="number"
                            placeholder="MRP"
                            value={item.product_mrp || ""}
                            className={styles.rowInput}
                            onChange={(e) =>
                              handleProductChange(
                                item.subid,
                                "product_mrp",
                                e.target.value
                              )
                            }
                          />
                        </td>
                      </tr>

                      {/* SUB ROWS */}
                      {expandedRow === item.subid &&
                        item?.sizes?.map((size) => (
                          <tr key={size.id} className={styles.subRow}>
                            {/* SIZE */}
                            <td>
                              <div className={styles.subProductCell}>
                                <div className={styles.subLine} />

                                <span className={styles.sizeText}>
                                  {size.sname}
                                </span>
                              </div>
                            </td>

                            {/* BASE RATE */}
                            <td>
                              <Form.Control
                                size="sm"
                                type="number"
                                placeholder="Base"
                                value={size.base_rate || ""}
                                className={styles.subInput}
                                onChange={(e) =>
                                  handleSizeChange(
                                    item.subid,
                                    size.id,
                                    "base_rate",
                                    e.target.value
                                  )
                                }
                              />
                            </td>

                            {/* MARGIN */}
                            <td>
                              <Form.Control
                                size="sm"
                                type="number"
                                placeholder="%"
                                value={size.margin || ""}
                                className={styles.subInput}
                                onChange={(e) =>
                                  handleSizeChange(
                                    item.subid,
                                    size.id,
                                    "margin",
                                    e.target.value
                                  )
                                }
                              />
                            </td>

                            {/* SALE RATE */}
                            <td>
                              <Form.Control
                                size="sm"
                                type="number"
                                placeholder="Sale"
                                value={size.sale_rate || ""}
                                className={styles.subInput}
                                onChange={(e) =>
                                  handleSizeChange(
                                    item.subid,
                                    size.id,
                                    "sale_rate",
                                    e.target.value
                                  )
                                }
                              />
                            </td>

                            {/* PRODUCT MRP */}
                            <td>
                              <Form.Control
                                size="sm"
                                type="number"
                                placeholder="MRP"
                                value={size.product_mrp || ""}
                                className={styles.subInput}
                                onChange={(e) =>
                                  handleSizeChange(
                                    item.subid,
                                    size.id,
                                    "product_mrp",
                                    e.target.value
                                  )
                                }
                              />
                            </td>
                          </tr>
                        ))}
                    </React.Fragment>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="text-center py-4">
                      No Product Data Found
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkPriceTableList;
